
import React, { useState, useRef } from 'react';
import { VendorProfile, VendorStatus, SubscriptionPlan, Neighborhood, Category, UserRole } from '../types';
import Logo from './Logo';
import ImageCropper from './ImageCropper';

interface RegistrationProps {
  neighborhoods: Neighborhood[];
  categories: Category[];
  onComplete: (vendor: VendorProfile) => Promise<boolean>;
  onCancel: () => void;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const VendorRegistration: React.FC<RegistrationProps> = ({ neighborhoods, categories, onComplete, onCancel, onToast }) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    responsible: '',
    email: '',
    password: '',
    confirmPassword: '',
    whatsapp: '',
    instagram: '',
    neighborhood_id: '',
    category_id: '',
    bio: '',
    avatar_url: ''
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateOTP = () => Math.floor(100000 + Math.random() * 900000).toString();

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhone(e.target.value);
    setFormData({ ...formData, whatsapp: masked });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.responsible.trim()) {
      onToast("O nome do responsável é obrigatório.", "error");
      return;
    }

    if (formData.email.toLowerCase() !== confirmEmail.toLowerCase()) {
      onToast("Os e-mails digitados não coincidem.", "error");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      onToast("As senhas digitadas são diferentes.", "error");
      return;
    }

    if (formData.password.length < 6) {
      onToast("Senha muito curta! Use no mínimo 6 dígitos.", "error");
      return;
    }

    const cleanWhatsapp = formData.whatsapp.replace(/\D/g, '');
    if (cleanWhatsapp.length < 10) {
      onToast("Telefone inválido. Digite o DDD + número.", "error");
      return;
    }

    setIsSending(true);
    const otpCode = generateOTP();

    const selectedNeighborhood = neighborhoods.find(n => n.id === formData.neighborhood_id)?.name;
    const selectedCategory = categories.find(c => c.id === formData.category_id)?.name;
    
    let emailSent = false;
    try {
      const emailRes = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          storeName: formData.name,
          responsibleName: formData.responsible,
          code: otpCode,
          whatsapp: formData.whatsapp,
          neighborhoodName: selectedNeighborhood,
          categoryName: selectedCategory
        })
      });

      if (emailRes.ok) {
        emailSent = true;
      } else {
        onToast("Aviso: O e-mail de validação pode atrasar ou falhar.", "info");
      }
    } catch (err) {
      console.error("Falha na chamada da API de e-mail:", err);
    }

    const neighborhood = neighborhoods.find(n => n.id === formData.neighborhood_id);
    const newVendorId = generateUUID();

    const newVendor: VendorProfile = {
      id: newVendorId,
      name: formData.name,
      responsible: formData.responsible,
      email: formData.email,
      password: formData.password,
      whatsapp: cleanWhatsapp, 
      instagram: formData.instagram,
      neighborhood_id: formData.neighborhood_id,
      category_id: formData.category_id,
      slug: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      avatar_url: formData.avatar_url || `https://picsum.photos/seed/${newVendorId}/200`,
      status: VendorStatus.PENDING,
      plan: SubscriptionPlan.FREE,
      rating: 5.0,
      location: neighborhood?.name || '',
      created_at: new Date().toISOString(),
      role: UserRole.OWNER,
      email_verified: false,
      bio: `VCODE:${otpCode}|${formData.bio}` 
    };

    const success = await onComplete(newVendor);

    if (success) {
      setIsSuccess(true);
      if (emailSent) {
        onToast("Cadastro realizado! Verifique sua caixa de entrada.", "success");
      } else {
        onToast("Cadastro realizado, mas houve um problema no envio do e-mail.", "error");
      }
    } else {
      setIsSending(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-[3.5rem] p-12 text-center shadow-2xl animate-in zoom-in duration-500">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
            <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
          </div>
          <h2 className="text-2xl font-title font-black text-brand-navy uppercase italic mb-4 italic">Quase pronto!</h2>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed font-medium">
            Sua conta foi criada. Verifique seu e-mail <span className="text-brand-navy font-bold">{formData.email}</span> para obter o código de ativação.
          </p>
          <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
             <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest leading-tight">DICA: Verifique sua caixa de SPAM caso não receba o e-mail em instantes.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 py-12">
      {tempImage && (
        <ImageCropper 
          image={tempImage} 
          cropShape="round" 
          aspect={1} 
          onComplete={(cropped) => { setFormData({...formData, avatar_url: cropped}); setTempImage(null); }} 
          onCancel={() => setTempImage(null)} 
        />
      )}

      <div className="w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 animate-in zoom-in duration-300">
        <div className="bg-white p-10 text-center border-b border-gray-50 flex flex-col items-center">
          <Logo className="h-14 mb-4" />
          <h2 className="text-xl font-title font-black text-brand-navy uppercase italic">Crie sua Vitrine Local</h2>
          <p className="text-gray-400 text-sm font-medium">Junte-se à maior rede de empreendedores de Guarulhos.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 sm:p-10">
          <div className="mb-10 flex flex-col items-center">
             <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 text-center">Logo da sua Loja</label>
             <div 
               onClick={() => !isSending && fileInputRef.current?.click()}
               className={`relative w-44 h-44 sm:w-52 sm:h-52 rounded-[3.5rem] bg-inputGray border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer overflow-hidden group hover:border-brand-blue transition-all shadow-inner ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
             >
               {formData.avatar_url ? (
                 <img src={formData.avatar_url} className="w-full h-full object-cover" alt="Logo preview" />
               ) : (
                 <div className="text-center p-6">
                   <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm group-hover:scale-110 transition-transform">
                    <svg className="w-8 h-8 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                   </div>
                   <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block">Upload de Logo</span>
                 </div>
               )}
             </div>
             <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Nome da Empresa</label>
              <input 
                disabled={isSending}
                type="text" required placeholder="Ex: Guarulhos Tech Store"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} 
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Nome do Responsável</label>
              <input 
                disabled={isSending}
                type="text" required placeholder="Nome completo de quem responde pela loja"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.responsible} onChange={e => setFormData({...formData, responsible: e.target.value})} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Instagram (@) - Opcional</label>
              <input 
                disabled={isSending}
                type="text" placeholder="@sua_loja"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.instagram} onChange={e => setFormData({...formData, instagram: e.target.value})} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">WhatsApp</label>
              <input 
                disabled={isSending}
                type="tel" required placeholder="(11) 99999-9999"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.whatsapp} 
                onChange={handlePhoneChange} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">E-mail Profissional</label>
              <input 
                disabled={isSending}
                type="email" required placeholder="loja@email.com"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Confirmar E-mail</label>
              <input 
                disabled={isSending}
                type="email" required placeholder="Repita o e-mail"
                className={`w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50 ${confirmEmail && confirmEmail.toLowerCase() !== formData.email.toLowerCase() ? 'ring-2 ring-red-400' : ''}`} 
                value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Criar Senha</label>
              <input 
                disabled={isSending}
                type="password" required placeholder="Mínimo 6 dígitos"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} 
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Confirmar Senha</label>
              <input 
                disabled={isSending}
                type="password" required placeholder="Repetir senha"
                className="w-full p-4 rounded-xl border-none bg-inputGray font-medium text-brand-navy disabled:opacity-50" 
                value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} 
              />
            </div>

            <div className="md:col-span-2 grid grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Bairro</label>
                <select disabled={isSending} required className="w-full p-4 rounded-xl border-none bg-inputGray appearance-none font-medium text-brand-navy disabled:opacity-50" value={formData.neighborhood_id} onChange={e => setFormData({...formData, neighborhood_id: e.target.value})}>
                  <option value="">Selecione...</option>
                  {neighborhoods.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">Categoria</label>
                <select disabled={isSending} required className="w-full p-4 rounded-xl border-none bg-inputGray appearance-none font-medium text-brand-navy disabled:opacity-50" value={formData.category_id} onChange={e => setFormData({...formData, category_id: e.target.value})}>
                  <option value="">Selecione...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="md:col-span-2 space-y-4 pt-8">
              <button 
                type="submit" 
                disabled={isSending}
                className="w-full bg-brand-blue text-white font-title font-black py-6 rounded-2xl shadow-xl shadow-brand-blue/10 hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3 disabled:opacity-70"
              >
                {isSending ? 'Enviando Validação...' : 'Finalizar Cadastro'}
              </button>
              <button type="button" onClick={onCancel} className="w-full text-gray-300 text-[10px] font-black uppercase tracking-widest hover:text-brand-navy transition-colors py-2">
                Voltar ao Início
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorRegistration;
